/** @jsxImportSource preact */
import type { JSX } from 'preact'
import { useEffect, useState } from 'preact/hooks'
import type { NdcCancelOffer, NdcCancelQuote } from '@plt/refund-quote'

const formatMoney = (amount: number, currency?: string) => `${currency ?? ''} ${amount.toFixed(2)}`.trim()

const formatExpiry = (expiresAt?: string) =>
  expiresAt ? new Date(expiresAt).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' }) : undefined

const isExpired = ({ expiresAt }: NdcCancelOffer, now: Date) => !!expiresAt && new Date(expiresAt) <= now

// Sabre signs the amount due from the traveller's side, so it is described rather than shown raw.
function describeAmountDue({ amountDue, currency }: NdcCancelOffer) {
  if (amountDue < 0) return `${formatMoney(-amountDue, currency)} back to the traveller`
  if (amountDue > 0) return `traveller would owe ${formatMoney(amountDue, currency)}`
  return 'nothing due either way'
}

function OfferRow({ offer, now }: { offer: NdcCancelOffer; now: Date }) {
  const expiry = formatExpiry(offer.expiresAt)
  return (
    <li className="text-sm text-indigo-800">
      <span className="font-semibold">{offer.offerType}</span>: original{' '}
      {formatMoney(offer.originalAmount, offer.currency)}, {describeAmountDue(offer)}, penalty{' '}
      {formatMoney(offer.penalty, offer.currency)}
      {expiry && (
        <span className={isExpired(offer, now) ? 'text-red-700' : 'text-indigo-600'}>
          {' '}
          ({isExpired(offer, now) ? 'expired' : 'expires'} {expiry})
        </span>
      )}
    </li>
  )
}

// What the quote says about voiding, without claiming more than it knows: a failed quote says
// nothing about availability, and an expired VOID offer is history, not a current option.
function voidAvailability(quote: NdcCancelQuote, now: Date): string {
  if (quote.error) return 'Void availability is unknown — the quote failed. Reload the quote before choosing void.'
  const voids = quote.offers.filter(({ offerType }) => offerType === 'VOID')
  if (voids.some(offer => !isExpired(offer, now))) return 'A void is still available.'
  if (voids.length) return 'The void offer has expired — reload the quote to see whether a void is still available.'
  return 'No void is offered — a void cancellation will be refused; process it as a full cancellation (refund).'
}

// setTimeout overflows past ~24.8 days; an offer that far out just re-arms on the next render.
const MAX_TIMEOUT_MS = 2 ** 31 - 1

export function NdcCancelQuotePanel({ quote }: { quote: NdcCancelQuote }): JSX.Element {
  const [now, setNow] = useState(() => new Date())

  // The screen can stay open past an offer's expiry; redraw then so "A void is still available"
  // doesn't outlive the offer.
  useEffect(() => {
    const nextExpiry = quote.offers
      .map(({ expiresAt }) => (expiresAt ? new Date(expiresAt).getTime() : NaN))
      .filter(expiry => expiry > now.getTime())
      .sort((a, b) => a - b)[0]
    if (nextExpiry === undefined) return

    const timer = setTimeout(() => setNow(new Date()), Math.min(nextExpiry - now.getTime(), MAX_TIMEOUT_MS))
    return () => clearTimeout(timer)
  }, [quote, now])

  return (
    <div className="p-4 bg-indigo-50 border-l-4 border-indigo-500 rounded-md space-y-1">
      <p className="text-sm font-semibold text-indigo-900">Airline NDC cancel quote — order {quote.orderId}</p>
      {quote.error && <p className="text-sm text-red-700">Sabre could not quote this cancellation: {quote.error}</p>}
      {quote.offers.length > 0 ? (
        <ul className="space-y-0.5">
          {quote.offers.map(offer => (
            <OfferRow
              key={`${offer.offerType}-${offer.expiresAt}`}
              offer={offer}
              now={now}
            />
          ))}
        </ul>
      ) : (
        !quote.error && <p className="text-sm text-indigo-800">The airline offered no way to cancel this order.</p>
      )}
      <p className="text-xs text-indigo-700">
        {voidAvailability(quote, now)} The cancellation asks the airline again when it runs, so amounts can move.
      </p>
    </div>
  )
}
